import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig, AxiosError } from "axios";
import axiosRetry, { exponentialDelay } from "axios-retry";
import dayjs from "dayjs";
import dayjsPluginUTC from "dayjs/plugin/utc";

import { config } from "../config";
import { getRuntimeConfig } from "../config/runtime-config";
import { trackApiRequest, trackException } from "../services/applicationInsights";
import { RootState, store } from "../store";

dayjs.extend(dayjsPluginUTC);

function getRootState(): RootState {
  return store.getState();
}

export const app_uri: string = config.apiBaseUrl;

const axiosClient = (): AxiosInstance => {
  const axiosClient = axios.create();

  axiosRetry(axiosClient, {
    retries: 0, // Disable automatic retries
    retryDelay: exponentialDelay,
    onRetry(retryCount, error, requestConfig) {
      console.log(error);
      console.log(`Retry attempt #${retryCount} for ${requestConfig.url}`);
    },
  });

  function doConfig(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
    const state = getRootState();
    const token = state.auth.token;
    const runtimeConfig = getRuntimeConfig();

    config.headers = config.headers || {};
    config.headers["Content-Type"] = config.data instanceof FormData ? "multipart/form-data" : "application/json";
    config.headers["Access-Control-Allow-Origin"] =
      runtimeConfig.nodeEnv === "development" ? "*" : "*.campaustralia.com.au";
    config.headers["request-startTime"] = dayjs().utc().format();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  }

  function responseTime(response: AxiosResponse): AxiosResponse {
    const start = response.config.headers ? response.config.headers["request-startTime"]?.toString() : dayjs().utc();
    const end = dayjs().utc();
    const milliseconds = end.diff(start, "millisecond");
    response.headers["request-duration"] = milliseconds.toString();

    // Track successful API request with ApplicationInsights
    const url = response.config.url || "unknown";
    const method = response.config.method?.toUpperCase() || "GET";
    trackApiRequest(`${method} ${url}`, url, milliseconds, response.status, true, {
      method,
      statusCode: response.status,
      endpoint: url,
    });

    console.log(`[API Tracking] ${method} ${url} - ${response.status} (${milliseconds}ms)`);

    return response;
  }

  function handleResponseError(error: AxiosError): Promise<never> {
    // Track API errors with ApplicationInsights
    if (error.response) {
      const url = error.config?.url || "unknown";
      const method = error.config?.method?.toUpperCase() || "GET";
      const start = error.config?.headers ? error.config.headers["request-startTime"]?.toString() : dayjs().utc();
      const end = dayjs().utc();
      const milliseconds = end.diff(start, "millisecond");

      trackApiRequest(`${method} ${url}`, url, milliseconds, error.response.status, false, {
        method,
        statusCode: error.response.status,
        endpoint: url,
        errorMessage: error.message,
      });

      // Track as exception if it's a 5xx server error
      if (error.response.status >= 500) {
        trackException(new Error(`API Error: ${error.response.status} ${method} ${url}`));
      }
    } else if (error.request) {
      // Network error (no response received)
      trackException(new Error(`Network Error: ${error.message}`));
    }

    return Promise.reject(error);
  }

  axiosClient.interceptors.request.use((config: InternalAxiosRequestConfig) => doConfig(config));
  axiosClient.interceptors.response.use((response: AxiosResponse) => responseTime(response), handleResponseError);

  return axiosClient;
};

export default axiosClient;
